import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useTheme
} from '@mui/material'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import Snapshot3D from './3DModel'
import { formatMacAddress } from '../../utils/string'
import type { PairedRuuviTag, RuuviTagAcceleration, RuuviTag } from '../../types'
import useDialog from '../../hooks/useDialog'
import { LoadingButton } from '@mui/lab'
import { getRuuviTagAccelerationNow, getRuuviTagConditionNow } from '../../services/ruuviTagService'
import { useDispatch } from 'react-redux'
import { createErrorNotification } from '../../redux/slices/notifications'

export interface RuuviTagScannedListItemButtonProps {
  tag: PairedRuuviTag
  onSelect: (tag: RuuviTag) => void
  isFetching?: boolean
  onExitInstallation: () => void
}

function computeRotationDegFromTag(tag: PairedRuuviTag) {
  const accel: RuuviTagAcceleration | undefined = tag.latest?.acceleration
  const rawX = accel?.xAvg ?? accel?.xMax ?? 0
  const rawY = accel?.yAvg ?? accel?.yMax ?? 0
  const rawZ = accel?.zAvg ?? accel?.zMax ?? 0
  const toDeg = (value: unknown) => {
    const numeric = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(numeric)) return 0
    return Math.max(-180, Math.min(180, numeric / 5))
  }
  return { x: toDeg(rawX), y: toDeg(rawY), z: toDeg(rawZ) }
}

export default function RuuviTagScannedListItemButton({
  tag: initialTag,
  onSelect,
  isFetching: _isFetching,
  onExitInstallation
}: RuuviTagScannedListItemButtonProps) {
  const { t } = useTranslation('installation')
  const theme = useTheme()
  const { open: fullscreenOpen, openDialog, closeDialog } = useDialog()
  const dispatch = useDispatch()
  const [tag, setTag] = useState<PairedRuuviTag>(initialTag)
  const [isRefreshing, setIsRefreshing] = useState(false)

  useEffect(() => {
    setTag(initialTag)
  }, [initialTag])

  const accel = tag.latest?.acceleration
  const x = accel?.xAvg ?? accel?.xMax ?? '—'
  const y = accel?.yAvg ?? accel?.yMax ?? '—'
  const z = accel?.zAvg ?? accel?.zMax ?? '—'
  const rssi = tag.latest?.condition?.rssi ?? '—'
  const fmt = (value: unknown) => (typeof value === 'number' ? value.toString() : value)

  const handleOpenDialog = (event?: React.MouseEvent) => {
    event?.stopPropagation()
    openDialog()
  }

  const handleSelectTag = () => {
    onSelect(tag)
    closeDialog()
  }

  const handleExitInstallation = () => {
    closeDialog()
    onExitInstallation()
  }

  const handleRefreshOrientation = async () => {
    if (isRefreshing) return
    const tagId = tag.id
    setIsRefreshing(true)
    try {
      const [conditionResult, accelerationResult] = await Promise.allSettled([
        getRuuviTagConditionNow(tagId),
        getRuuviTagAccelerationNow(tagId)
      ])

      setTag((prev) => ({
        ...prev,
        latest: {
          ...prev?.latest,
          condition:
            conditionResult.status === 'fulfilled' ? conditionResult.value : prev.latest?.condition,
          acceleration:
            accelerationResult.status === 'fulfilled'
              ? accelerationResult.value
              : prev.latest?.acceleration
        }
      }))
    } catch (_error) {
      dispatch(createErrorNotification(t('somethingWentWrong')))
    } finally {
      setIsRefreshing(false)
    }
  }

  const secondaryText = useMemo(() => {
    const asset = tag.asset
    if (!asset) {
      return t('unknown', { defaultValue: 'Unknown' })
    }
    if (asset.projectId == null) {
      return t('notInstalled', { defaultValue: 'Not installed' })
    }
    const parts: string[] = []
    const levelName = asset.level?.name
    const liftName = asset.lift?.name
    if (levelName) parts.push(levelName)
    if (liftName) parts.push(liftName)
    if (parts.length > 0) return parts.join(' / ')
    return asset.name || t('installed', { defaultValue: 'Installed' })
  }, [tag.asset, t])

  return (
    <>
      <ListItemButton
        onClick={() => onSelect(tag)}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 220px' },
          alignItems: 'center',
          gap: 1,
          py: 1.25,
          px: 1.5,
          mb: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: theme.palette.mode === 'light' ? '#f7f9fc' : '#1b2635',
          '&:hover': { backgroundColor: theme.palette.mode === 'light' ? '#eef3fb' : '#223041' }
        }}
      >
        <ListItemText
          primary={formatMacAddress(tag.id)}
          secondary={secondaryText}
          primaryTypographyProps={{ sx: { order: '0 !important' } }}
          secondaryTypographyProps={{ sx: { order: '1 !important' } }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            gap: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}
        >
          <Box sx={{ width: { xs: '100%', sm: 'auto' }, textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography variant="body2" component="div">
              {fmt(x)} / {fmt(y)} / {fmt(z)}
            </Typography>
            {tag.latest?.condition && (
              <Typography variant="body2" component="div">
                {tag.latest.condition.temperatureAvg.toFixed(1)}°C
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              RSSI: {fmt(rssi)}
            </Typography>
          </Box>

          <Box
            component="div"
            role="button"
            tabIndex={0}
            aria-label={String(t('openFullScreen'))}
            onClick={handleOpenDialog}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.stopPropagation()
                handleOpenDialog()
              }
            }}
            sx={{
              width: { xs: '95%', sm: 120 },
              minWidth: { sm: 120 },
              aspectRatio: '1 / 1',
              height: { xs: 'auto', sm: 80 },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: theme.palette.mode === 'light' ? '#f7f9fc' : '#1b2635',
              borderRadius: 1,
              overflow: 'hidden',
              cursor: 'pointer',
              outline: 'none',
              '&:focus-visible': { boxShadow: '0 0 0 2px rgba(255,255,255,0.4)' }
            }}
          >
            <Snapshot3D
              modelUrl={'/static/ruuvitag-pro-simplified-enclosure.glb'}
              rotationDeg={computeRotationDegFromTag(tag)}
              background={theme.palette.mode === 'light' ? 0xf7f9fc : 0x1b2635}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>

          <IconButton
            size="medium"
            onClick={handleOpenDialog}
            aria-label={t('openFullScreen')}
            sx={{ width: 36, height: 36, mt: { xs: 1, sm: 0 } }}
          >
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Box>
      </ListItemButton>

      <Dialog fullScreen open={fullscreenOpen} onClose={closeDialog}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 2, sm: 3 }
          }}
        >
          <Button variant="contained" color="error" onClick={handleExitInstallation}>
            {t('exitInstallation')}
          </Button>
          <IconButton aria-label={t('close')} onClick={closeDialog}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
            p: { xs: 2, sm: 3 },
            maxHeight: '100vh',
            boxSizing: 'border-box'
          }}
        >
          <Box
            sx={{
              width: { xs: '95vw', sm: '90vw', md: '80vw' },
              height: { xs: '60vh', sm: '70vh', md: '80vh' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Snapshot3D
              modelUrl={'/static/ruuvitag-pro-simplified-enclosure.glb'}
              rotationDeg={computeRotationDegFromTag(tag)}
              interactive={true}
              background={theme.palette.mode === 'light' ? 0xf7f9fc : 0x1b2635}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ width: '100%', justifyContent: 'center' }}
          >
            <Button variant="contained" color="primary" size="large" onClick={handleSelectTag}>
              {t('selectThisTag')}
            </Button>
            <LoadingButton
              variant="outlined"
              color="inherit"
              size="large"
              onClick={handleRefreshOrientation}
              loading={isRefreshing}
              disabled={isRefreshing}
            >
              {t('refreshOrientation')}
            </LoadingButton>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}
